import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useGetV4ProductReportByProduct } from "../../../../modules/perf_reports/api/product/v4ProductReportByProduct";

const EMPTY_MAP: ReadonlyMap<string, string> = new Map();

// Guards against an unbounded request if a plan somehow carries a huge selection; the callers that
// need asins (creative previews, the duplicate-asin check) only ever look at a handful of products.
const MAX_PRODUCTS = 1000;

/**
 * Resolves asins for a bounded set of product ids — the products a plan has selected, never the whole
 * catalogue. Ad plans send product ids and let the server resolve asins, so the form only needs this
 * where it has to show or validate an asin before submitting.
 */
export function useAsinsByProductId(
  productIds?: Array<number | string | null | undefined>,
): ReadonlyMap<string, string> {
  const getReportOptions = useGetV4ProductReportByProduct();

  const ids = useMemo(
    () =>
      [...new Set((productIds ?? []).filter((id) => id != null).map((id) => String(id)))].sort().slice(0, MAX_PRODUCTS),
    [productIds],
  );

  const { data } = useQuery({
    ...getReportOptions({
      pagination: { pageIndex: 0, pageSize: ids.length || 1 },
      sorting: [],
      filters: [{ id: "product_id", value: { op: "anyof", value: ids } }],
    }),
    enabled: ids.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  return useMemo(() => {
    if (!data?.data.length) {
      return EMPTY_MAP;
    }
    return new Map(data.data.map((row) => [String(row.product_id), row.asin]));
  }, [data]);
}
